<script setup>
import { computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { Tooltip } from "@/components/ui/tooltip";
import ResultsImportancePerformanceChart from "../Charts/ResultsImportancePerformanceChart.vue";
import AnalysisCardShell from "./AnalysisCardShell.vue";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const store = useResultsV2Store();
const { importancePerformanceData, availableCards, currentView } = storeToRefs(store);

const content = computed(() => props.card.content || {});
const isConfigured = computed(
  () => content.value.global_question_id && content.value.matrix_question_ids?.length,
);

// Find label for global question
const globalQuestionLabel = computed(() => {
  const qid = content.value.global_question_id;
  if (!qid) return null;
  const card = availableCards.value.find((c) => c.questions?.some((q) => q.id === qid));
  return card?.name || `Q${qid}`;
});

// Resolve the matrix card from availableCards — its questions contain id/name per sub-question
const matrixMeta = computed(() => {
  const ids = content.value.matrix_question_ids;
  if (!ids?.length) return null;
  return availableCards.value.find(
    (c) => c.type === "q_matrix" && (c.questions || []).some((q) => ids.includes(q.id)),
  );
});

// Chart data from store
const chartResults = computed(() => importancePerformanceData.value[props.card.id] || []);

const populations = computed(() => {
  const questions = matrixMeta.value?.questions || [];
  const matrixItems = matrixMeta.value?.specs?.items || [];
  return chartResults.value.map((result, idx) => ({
    label: result.label,
    color: result.color || POPULATION_AUTO_COLORS[idx] || POPULATION_AUTO_COLORS[0],
    items: (result.data?.items || []).map((item) => {
      const meta = questions.find((q) => q.id === item.question_id);
      const title = matrixItems.find((matrixItem) => matrixItem.token === meta?.item_token)?.title;
      const label = title
        ? (typeof title === "object" ? Object.values(title)[0] : title)
        : meta?.name || null;
      return { ...item, label: label || `Q${item.question_id}` };
    }),
  }));
});

const thresholds = computed(() => {
  if (content.value.threshold_mode === "manual") {
    return {
      satisfaction: content.value.satisfaction_threshold ?? 5,
      importance: content.value.importance_threshold ?? 0.3,
    };
  }
  const first = chartResults.value[0];
  return first?.data?.thresholds || { satisfaction: 5, importance: 0.3 };
});

const respondents = computed(() => {
  const first = chartResults.value[0];
  return first?.data?.respondents || 0;
});

const r2 = computed(() => chartResults.value[0]?.data?.r2 ?? null);
const r2Respondents = computed(() => chartResults.value[0]?.data?.r2_respondents || 0);

const halo = computed(() => chartResults.value[0]?.data?.halo || null);
const isBlocked = computed(() => halo.value?.status === "blocked");

// The store holds no entry until the first response lands; an entry with no items
// is a real answer (empty base, unsupported matrix) — not a loading state.
const state = computed(() => {
  if (!isConfigured.value) return "unconfigured";
  if (!chartResults.value.length) return "loading";
  return populations.value[0]?.items.length ? "ready" : "empty";
});

// Fetch data when configuration is set
function fetchData() {
  if (!isConfigured.value) return;
  store.fetchImportancePerformanceData(props.card.id, {
    globalQuestionId: content.value.global_question_id,
    matrixQuestionIds: content.value.matrix_question_ids,
  });
}

watch(
  () => [
    content.value.global_question_id,
    content.value.matrix_question_ids,
    currentView.value?.population_configs,
  ],
  () => fetchData(),
  { immediate: true, deep: true },
);
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :respondents="isConfigured ? respondents : null"
    :state="state"
    state-message="Select a global question and a matrix to configure this analysis."
    respondents-hint="Largest item base — each item is correlated with the global question on the respondents who answered both (pairwise)."
    description="Positions each item of a matrix by its average score and by its correlation with a global score, to show what to fix first. Correlations stop discriminating under a halo; the card then blocks and the Key drivers card takes over with relative weights.">
    <template v-if="isConfigured" #subtitle>
      <p class="text-xs text-muted-foreground">
        {{ globalQuestionLabel }} × {{ matrixMeta?.name || 'Matrix' }}
        <template v-if="r2 != null">
          ·
          <Tooltip
            :content="`Share of the score's variation the items explain together, on the ${r2Respondents} respondents who answered the score and every item of the model; an item answered by fewer than half of them is left out of it. Below 0.3, most of what moves the score is outside this matrix.`"
            :delay-duration="300">
            <span class="tabular-nums">R² {{ r2.toFixed(2) }}</span>
          </Tooltip>
        </template>
      </p>
    </template>

    <!-- Halo warning banner -->
    <template v-if="halo && halo.status !== 'ok'">
      <div
        :class="[
          'rounded-md border px-3 py-2 text-xs leading-snug',
          halo.status === 'blocked'
            ? 'border-status-error-border bg-status-error-soft text-status-error-strong'
            : 'border-status-warning-border bg-status-warning-soft text-status-warning-strong',
        ]">
        <template v-if="halo.status === 'blocked'">
          <span class="font-semibold">Analysis unavailable — halo effect detected.</span> Respondents tend to rate all items in the same direction as their overall satisfaction, so the correlations cannot tell which items drive their experience. The Key drivers card ranks the items with relative weights, which hold up under a halo.
        </template>
        <template v-else-if="halo.status === 'warning_discrimination'">
          <span class="font-semibold">Low importance discrimination.</span> The importance scores are too close together to clearly differentiate priorities. Quadrant positions may not be reliable.
        </template>
        <template v-else>
          <span class="font-semibold">Halo effect detected.</span> Respondents tend to rate all items similarly to their overall satisfaction. The matrix is displayed but results should be interpreted with caution.
        </template>
      </div>
    </template>

    <ResultsImportancePerformanceChart
      v-if="!isBlocked"
      :populations="populations"
      :thresholds="thresholds"
      :threshold-mode="content.threshold_mode || 'auto'"
    />
  </AnalysisCardShell>
</template>
